import { PermissionEnum } from '@Application/Security/Roles';
import { theme } from '@Guidelines/Theme/theme';
import ModelCard from '@UI/Pages/Administration/AiEngines/Sections/ModelsSection/ModelCard';
import { openModelSidebar } from '@UI/Pages/Administration/AiEngines/Signals/modelSidebarSignal';
import { css } from '@emotion/react';
import { Cpu, Plus, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import Filters from '@UI/Pages/Administration/AiEngines/Sections/ModelsSection/Filters';
import AiModelFilterView from '@UI/Pages/Administration/AiEngines/Sections/ModelsSection/Filters/AiModelFilterView';
import aiModelFiltersSignal, {
	setAiModelFilters,
} from '@UI/Pages/Administration/AiEngines/Sections/ModelsSection/Filters/Signals/aiModelFiltersSignal';
import filterAiModels from '@UI/Pages/Administration/AiEngines/Sections/ModelsSection/Helpers/filterAiModels';
import type AiModelsListView from '@UI/Store/AiConfiguration/Views/AiModelsListView';
import EmptyState from '@Guidelines/Component/EmptyState';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

const headerRowStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	flex-wrap: wrap;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	margin: 0;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const cardsGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(22rem, 100%), 1fr));
	gap: 1.25rem;
`;

type Props = {
	models: AiModelsListView;
};

/**
 * Self-contained block: loading, error and the account-wide empty states are the mounting screen's job
 * (`AiEnginesContent.tsx`); the read gate is the tab's. Reads `aiConfigurationStore.providers` and the
 * `models` already loaded by that screen; never fetches on its own. Without any declared provider no
 * model can exist, so it points to the providers tab. The list is filtered client-side by
 * `aiModelFiltersSignal`; the eyebrow keeps the total authorized count. The "Ajouter un modèle" action
 * is narrower than the read gate: `AI_MODEL_WRITE`. The card grid is written by hand rather than
 * through `grid-system`: each `ModelCard` must sit as a direct child of the grid for its own subgrid
 * alignment to hold.
 */
const ModelsSection: React.FC<Props> = ({ models }) => {
	const providers = useSelector((state) => state.aiConfigurationStore.providers);
	const filters = useSignalValue(aiModelFiltersSignal);

	const filteredModels = React.useMemo(() => filterAiModels(models.list, filters), [models, filters]);

	const handleCreateClick = React.useCallback(() => {
		openModelSidebar(null);
	}, []);

	const handleResetFiltersClick = React.useCallback(() => {
		setAiModelFilters(AiModelFilterView.createDefault());
	}, []);

	if (providers === null || providers.list.length === 0) {
		return (
			<EmptyState
				title="Aucun fournisseur d’IA n’est déclaré."
				description="Déclarez d’abord un fournisseur dans l’onglet Fournisseurs pour pouvoir ajouter des modèles."
				icon={<Cpu size={28} />}
			/>
		);
	}

	if (models.list.length === 0) {
		return (
			<EmptyState
				title="Aucun modèle n’est autorisé sur ce compte. Les traitements d’IA sont impossibles."
				icon={<Cpu size={28} />}
				action={
					<WithPermission permission={PermissionEnum.AI_MODEL_WRITE}>
						<Button
							type="button"
							label="Ajouter un modèle"
							icon={<Plus size={16} />}
							onClick={handleCreateClick}
						/>
					</WithPermission>
				}
			/>
		);
	}

	return (
		<section css={sectionStyle}>
			<div css={headerRowStyle}>
				<p css={eyebrowStyle}>
					CATALOGUE DE MODÈLES · {models.list.length} AUTORISÉ{models.list.length > 1 ? 'S' : ''}
				</p>
				<WithPermission permission={PermissionEnum.AI_MODEL_WRITE}>
					<Button
						type="button"
						label="Ajouter un modèle"
						icon={<Plus size={16} />}
						onClick={handleCreateClick}
					/>
				</WithPermission>
			</div>
			<Filters />
			<ConditionalLoader
				data={filteredModels.length > 0}
				fallback={
					<EmptyState
						title="Aucun résultat"
						description="Aucun modèle ne correspond à ces filtres."
						icon={<TriangleAlert size={28} />}
						action={
							<Button
								type="button"
								text
								label="Réinitialiser les filtres"
								onClick={handleResetFiltersClick}
							/>
						}
					/>
				}
			>
				<div css={cardsGridStyle}>
					{filteredModels.map((model) => (
						<ModelCard key={model.id} model={model} />
					))}
				</div>
			</ConditionalLoader>
		</section>
	);
};

export default ModelsSection;
